import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { AlertTriangle, Bell, Check, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { selectAppToken } from "../store/slices/appAuthSlice";
import { apiClient } from "../lib/api";
import { useNavigate } from "react-router-dom";
import Button from "./shared/Button";
import { RootState } from "../store/store";
import { fetchDevices } from "../lib/devices";
import { isAgentUpdateAvailable } from "../lib/agentVersion";
import { useLatestAgentVersion } from "../hooks/useLatestAgentVersion";
import OpenChangeObserver from "./shared/OpenChangeObserver";
import { RAIL_ROW, RAIL_ROW_IDLE } from "./layout/navItems";

//================================================================================
// 0. API LAYER & TYPES
//================================================================================
interface Notification {
  id: string;
  incident_id?: string;
  title: string;
  body: string;
  created_at: string;
  read_at: string | null;
}

interface NotificationItem extends Notification {
  isSynthetic?: boolean;
}

const getLocalDayKey = () => new Date().toISOString().slice(0, 10);

// --- API Functions ---
const fetchUnreadNotifications = async (): Promise<Notification[]> => {
  try {
    const response = await apiClient.get("/notifications");
    return response.data;
  } catch (error) {
    console.error("Error fetching unread notifications:", error);
    return [];
  }
};

const markNotificationAsRead = async (
  notificationId: string,
): Promise<void> => {
  try {
    await apiClient.post(`/notifications/${notificationId}/read`);
  } catch (error) {
    console.error("Error marking notification as read:", error);
  }
};

const markAllNotificationsAsRead = async (): Promise<void> => {
  try {
    await apiClient.post("/notifications/read/all");
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
  }
};

//================================================================================
// 1. HOOKS
//================================================================================
const useNotifications = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useSelector(selectAppToken);
  const user = useSelector((state: RootState) => state.appAuthReducer.user);

  // Fetch initial unread notifications
  const { data: notifications = [], isLoading } = useQuery<NotificationItem[]>({
    queryKey: ["notifications"],
    queryFn: fetchUnreadNotifications,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!token, // Only fetch if the user is authenticated
  });

  const { data: devices = [] } = useQuery({
    queryKey: ["devices"],
    queryFn: fetchDevices,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!token,
  });

  const latestAgentVersion = useLatestAgentVersion();

  const updatesAvailableCount = useMemo(
    () =>
      devices.filter((device) =>
        isAgentUpdateAvailable(device.agentVersion, latestAgentVersion),
      ).length,
    [devices, latestAgentVersion],
  );

  const digestStorageKey = useMemo(() => {
    const userScope = user?.id ?? "anonymous";
    const orgScope = user?.orgId ?? "unknown-org";
    return `insaion:agent-update-digest:${orgScope}:${userScope}:${getLocalDayKey()}`;
  }, [user?.id, user?.orgId]);

  const [isDigestDismissed, setIsDigestDismissed] = useState(false);

  useEffect(() => {
    try {
      setIsDigestDismissed(localStorage.getItem(digestStorageKey) === "1");
    } catch {
      setIsDigestDismissed(false);
    }
  }, [digestStorageKey]);

  const dismissDailyDigest = () => {
    try {
      localStorage.setItem(digestStorageKey, "1");
    } catch {
      // noop
    }
    setIsDigestDismissed(true);
  };

  const syntheticDigest = useMemo<NotificationItem | null>(() => {
    if (updatesAvailableCount <= 0 || isDigestDismissed) return null;
    const plural = updatesAvailableCount === 1 ? "device" : "devices";
    return {
      id: `agent-update-digest-${digestStorageKey}`,
      incident_id: undefined,
      title: "Agent update summary",
      body: `${updatesAvailableCount} ${plural} can be updated to ${latestAgentVersion}.`,
      created_at: new Date().toISOString(),
      read_at: null,
      isSynthetic: true,
    };
  }, [
    updatesAvailableCount,
    isDigestDismissed,
    digestStorageKey,
    latestAgentVersion,
  ]);

  // WebSocket connection for real-time updates with reconnect/backoff
  useEffect(() => {
    if (!token) return;

    let ws: WebSocket | null = null;
    let heartbeatInterval: ReturnType<typeof setTimeout>;
    let reconnectTimeout: ReturnType<typeof setTimeout>;
    let retryCount = 0;
    let closedByUser = false;

    const connect = () => {
      if (!token) return;
      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${wsProtocol}//${window.location.host}/api/v2/ws?token=${token}`;
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        retryCount = 0;
        // console.log("WebSocket connection established.");
        heartbeatInterval = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN)
            ws.send(JSON.stringify({ type: "ping" }));
        }, 30000);
      };

      ws.onclose = () => {
        // console.log("WebSocket connection closed.");
        clearInterval(heartbeatInterval);
        if (!closedByUser) {
          // Exponential backoff for reconnect
          retryCount++;
          const delay = Math.min(1000 * Math.pow(2, retryCount), 30000); // max 30s
          reconnectTimeout = setTimeout(connect, delay);
        }
      };

      ws.onerror = (error) => {
        // Only log error, let onclose handle reconnect
        console.warn("WebSocket error, will attempt reconnect:", error);
      };

      ws.onmessage = (event) => {
        if (event.data === '{"type":"pong"}') return;
        try {
          const messageData = JSON.parse(event.data) as {
            title?: string;
            body?: string;
            alarmId?: string;
            incidentId?: string;
          };

          // One toast per alarm: an alarm opening incidents for several
          // devices at once updates its toast instead of stacking more.
          toast.error(messageData.title, {
            id: `alarm-${messageData.alarmId ?? messageData.incidentId}`,
            description: messageData.body,
            action: {
              label: "View Incident",
              onClick: () => navigate("/alarms?tab=incidents"),
            },
          });

          // The engine stored a notification per user; load it with its id.
          queryClient.invalidateQueries({ queryKey: ["notifications"] });
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };
    };

    // Debounce initial connection to avoid racing with token acquisition
    reconnectTimeout = setTimeout(connect, 300);

    return () => {
      closedByUser = true;
      clearInterval(heartbeatInterval);
      clearTimeout(reconnectTimeout);
      if (
        ws &&
        (ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING)
      ) {
        ws.close();
      }
    };
  }, [token, queryClient]);

  // Mutation for marking a single notification as read
  const markAsReadMutation = useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: (_, notificationId) => {
      // Update the cache to reflect the change immediately
      queryClient.setQueryData<NotificationItem[]>(
        ["notifications"],
        (oldData = []) => oldData.filter((n) => n.id !== notificationId),
      );
    },
  });

  // Mutation for marking all as read
  const markAllAsReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      // Set the cache to an empty array
      queryClient.setQueryData<NotificationItem[]>(["notifications"], []);
    },
  });

  const mergedNotifications = useMemo<NotificationItem[]>(() => {
    if (!syntheticDigest) return notifications;
    return [syntheticDigest, ...notifications];
  }, [notifications, syntheticDigest]);

  const handleMarkAsRead = (notificationId: string) => {
    if (syntheticDigest && notificationId === syntheticDigest.id) {
      dismissDailyDigest();
      return;
    }
    markAsReadMutation.mutate(notificationId);
  };

  const handleMarkAllAsRead = () => {
    if (notifications.length > 0) {
      markAllAsReadMutation.mutate();
    }
    if (syntheticDigest) {
      dismissDailyDigest();
    }
  };

  const unreadCount = mergedNotifications.length;

  return {
    notifications: mergedNotifications,
    unreadCount,
    isLoading,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    isUpdating: markAsReadMutation.isPending || markAllAsReadMutation.isPending,
  };
};

//================================================================================
// 2. NOTIFICATION CENTER COMPONENT
//================================================================================
interface NotificationCenterProps {
  /** Icon-only, for the collapsed rail. Otherwise a labelled row. */
  collapsed?: boolean;
  /** Lets the rail hold its hover peek open while the panel is showing. */
  onOpenChange?: (open: boolean) => void;
}

export default function NotificationCenter({
  collapsed = false,
  onOpenChange,
}: NotificationCenterProps = {}) {
  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    isUpdating,
  } = useNotifications();

  return (
    <Popover>
      {({ open }) => (
        <>
          <OpenChangeObserver open={open} onOpenChange={onOpenChange} />
          <PopoverButton
            title="Notifications"
            className={`${RAIL_ROW} ${RAIL_ROW_IDLE} focus:outline-none`}
          >
            <span className="relative shrink-0">
              <Bell className="h-5 w-5" aria-hidden="true" />
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </span>
              )}
            </span>
            <span className={collapsed ? "sr-only" : "truncate"}>
              Notifications
            </span>
            {!collapsed && unreadCount > 0 && (
              <span className="ml-auto shrink-0 rounded-full bg-red-500/15 px-1.5 py-0.5 text-[0.6875rem] font-semibold leading-none text-red-400">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </PopoverButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <PopoverPanel
              portal
              anchor={{ to: "right end", gap: 12, padding: 12 }}
              className="z-[150] w-96 max-w-[calc(100vw-24px)] rounded-lg bg-neutral-800/90 shadow-2xl ring-1 ring-white/10 backdrop-blur-md focus:outline-none"
            >
              <div className="flex items-center justify-between border-b border-neutral-700 p-4">
                <h3 className="text-base font-semibold text-white">
                  Notifications
                </h3>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => markAllAsRead()}
                  disabled={unreadCount === 0 || isUpdating}
                >
                  Mark all as read
                </Button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {isLoading ? (
                  <div className="p-8 text-center text-sm text-neutral-500 flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="relative flex items-start gap-4 p-4 transition-colors hover:bg-neutral-700/50"
                    >
                      <div
                        className={`absolute left-0 top-0 bottom-0 w-0.5 ${notification.isSynthetic ? "bg-amber-400" : "bg-rose-500"}`}
                      ></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white flex items-center gap-1.5">
                          {notification.isSynthetic && (
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                          )}
                          {notification.title}
                        </p>
                        <p className="mt-1 text-sm text-neutral-400">
                          {notification.body}
                        </p>
                        <p className="mt-2 text-xs text-neutral-500">
                          {formatDistanceToNow(
                            new Date(notification.created_at),
                            { addSuffix: true },
                          )}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => markAsRead(notification.id)}
                        title={
                          notification.isSynthetic
                            ? "Dismiss for today"
                            : "Mark as read"
                        }
                        disabled={isUpdating}
                      >
                        <Check size={14} />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="p-8 text-center text-sm text-neutral-500">
                    You're all caught up!
                  </p>
                )}
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
